import { useEffect } from "react"
import { Building2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import { AvatarInput } from "@/components"
import { useToast, useSysConfig } from "@/hooks"
import { useUploadBrandLogoApi, brandResponseToBrand } from "@/hooks/api"
import { FetchStatus } from "@/hooks/useFetch"
import { useStore } from "@/store"
import { Brand } from "@/types"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"
import { getResourceUrl } from "@/lib/utils"

interface BrandLogoUploadProps {
  brand: Brand
  disabled?: boolean
  onLogoChange?: (brand: Brand) => void
}

const BrandLogoUpload = ({
  brand,
  disabled = false,
  onLogoChange,
}: BrandLogoUploadProps) => {
  const { t } = useTranslation()
  const config = useSysConfig()

  const { alertToast, successToast } = useToast()
  const [currentOrganization] = useStore((state) => [state.currentOrganization])

  const logoUrl = getResourceUrl(brand.logo || "", config.serverRoot)

  const {
    data: uploadRespData,
    error: uploadRespError,
    status: uploadRespStatus,
    fetchFn: uploadLogo,
  } = useUploadBrandLogoApi(currentOrganization?.id || "", brand.id)

  // Handle upload success
  useEffect(() => {
    if (!uploadRespData || !uploadRespData?.data) return

    const updatedBrand = brandResponseToBrand(uploadRespData.data)
    onLogoChange?.(updatedBrand)
    successToast({
      title: t("brands.messages.logoUploaded.title"),
      description: t("brands.messages.logoUploaded.description"),
    })
  }, [uploadRespData, onLogoChange, successToast, t])

  // Handle upload error
  useEffect(() => {
    if (!uploadRespError) return

    alertToast({
      title: t("brands.messages.error.title"),
      description: getUserFacingApiErrorMessage(
        uploadRespError,
        t,
        uploadRespData?.message || t("brands.messages.error.uploadLogo")
      ),
    })
  }, [uploadRespError, uploadRespData, alertToast, t])

  const handleFileChange = (files: File[]) => {
    const selectedFile = files[0]

    // Backend-allowed MIME types (see ALLOWED_LOGO_MIME_TYPES)
    const allowedMimeTypes = new Set([
      "image/jpeg",
      "image/png",
      "image/jpg",
      "image/pjpeg",
      "image/x-png",
    ])

    // Validate file type
    if (!allowedMimeTypes.has(selectedFile.type)) {
      alertToast({
        title: t("brands.logo.errors.invalidFileType.title"),
        description: t("brands.logo.errors.invalidFileType.description"),
      })
      return
    }

    // Validate file size (max 1MB)
    if (selectedFile.size > 1024 * 1024) {
      alertToast({
        title: t("brands.logo.errors.fileSizeTooLarge.title"),
        description: t("brands.logo.errors.fileSizeTooLarge.description"),
      })
      return
    }

    // Create FormData for upload
    const formData = new FormData()
    formData.append("logo", selectedFile)

    // Call upload API
    uploadLogo({
      body: formData,
    })
  }

  const isUploading = uploadRespStatus === FetchStatus.Loading

  return (
    <AvatarInput
      size="w-24 h-24"
      url={logoUrl}
      name={brand.name}
      subTitle={brand.name}
      fallback={<Building2 className="h-8 w-8 text-muted-foreground" />}
      disabled={disabled || isUploading}
      onChange={handleFileChange}
    />
  )
}

export default BrandLogoUpload
